import React, { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { skipToken } from '@reduxjs/toolkit/query/react';
import { produce } from 'immer';
import {
  SButton,
  SIcon,
  SInput,
  SNotification,
  SRadioGroup,
  SSelect,
  STable,
  STooltip,
} from '@chargebee/sting-react';
import { ConditionsGroup } from '@bbkAdminComponents/experiences/audiences/edit-rule/conditions-group';
import { selectAppId } from '@bbkAdminRedux/app/selectors';
import {
  OptimizationActionType,
  OptimizationAddonAdjustmentType,
  OptimizationAddonScope,
  OptimizationPriceAdjustmentType,
  OptimizationSliceHelper,
  optimizationsSlice,
  type OptimizationAction,
} from '@bbkAdminRedux/rtkq/optimizations.slice';
import { cs } from '@bbkAdminUtils/css-utils';
import { OptimizationActionsHelper } from './OptimizationActionsHelper';

type OptimizationActionFormProps = {
  value: OptimizationAction;
  onChange: (action: OptimizationAction) => void;
  actionsOnly?: boolean;
};

const addonAdjustmentTypeOptions = [
  {
    label: 'Increase by percentage',
    value: OptimizationAddonAdjustmentType.INCREASE_BY_PERCENTAGE,
  },
  {
    label: 'Increase by fixed amount',
    value: OptimizationAddonAdjustmentType.INCREASE_BY_FIXED_AMOUNT,
  },
  {
    label: 'Decrease by percentage',
    value: OptimizationAddonAdjustmentType.DECREASE_BY_PERCENTAGE,
  },
  {
    label: 'Decrease by fixed amount',
    value: OptimizationAddonAdjustmentType.DECREASE_BY_FIXED_AMOUNT,
  },
];

const isPlanPercentageAdj = (
  action: OptimizationAction
): action is Extract<
  OptimizationAction,
  { type: typeof OptimizationActionType.PLAN_PRICE_ADJUSTMENT }
> =>
  action.type === OptimizationActionType.PLAN_PRICE_ADJUSTMENT &&
  (action.adjustment_type ===
    OptimizationPriceAdjustmentType.DECREASE_BY_PERCENTAGE ||
    action.adjustment_type ===
      OptimizationPriceAdjustmentType.INCREASE_BY_PERCENTAGE);

const isAddonPercentageAdj = (t: string | undefined) =>
  t === OptimizationAddonAdjustmentType.INCREASE_BY_PERCENTAGE ||
  t === OptimizationAddonAdjustmentType.DECREASE_BY_PERCENTAGE;

/** Chargebee catalog prices are in the smallest currency unit (e.g. cents). */
const catalogPriceToMajorUnits = (price: number): number => price / 100;

const formatAddonPriceCell = (
  majorUnits: number | undefined,
  currencyCode: string | undefined
): string => {
  if (majorUnits === undefined || !Number.isFinite(majorUnits)) {
    return '—';
  }
  const cur = currencyCode?.trim() ?? '';
  const n = majorUnits.toFixed(2);
  return cur ? `${cur} ${n}` : n;
};

const previewNewAddonPriceMajorUnits = (
  currentMajor: number | undefined,
  adjustmentType: string | undefined,
  adjustmentValue: number | undefined
): number | undefined => {
  if (currentMajor === undefined || !Number.isFinite(currentMajor)) {
    return undefined;
  }
  const v = adjustmentValue ?? 0;
  switch (adjustmentType) {
    case OptimizationAddonAdjustmentType.INCREASE_BY_PERCENTAGE:
      return currentMajor * (1 + v / 100);
    case OptimizationAddonAdjustmentType.DECREASE_BY_PERCENTAGE:
      return currentMajor * (1 - v / 100);
    case OptimizationAddonAdjustmentType.INCREASE_BY_FIXED_AMOUNT:
      return currentMajor + v;
    case OptimizationAddonAdjustmentType.DECREASE_BY_FIXED_AMOUNT:
      return currentMajor - v;
    default:
      return undefined;
  }
};

export const OptimizationActionForm: React.FC<OptimizationActionFormProps> = ({
  value: action,
  onChange,
  actionsOnly,
}) => {
  const appKey = useSelector(selectAppId);

  const { data: plans = [] } = optimizationsSlice.endpoints.getPlans.useQuery(
    appKey ? { appKey } : skipToken
  );

  const { data: addons = [] } = optimizationsSlice.endpoints.getAddons.useQuery(
    appKey ? { appKey } : skipToken
  );

  const allPlansOptions = useMemo(
    () => OptimizationActionsHelper.getPlanOptions(plans),
    [plans]
  );

  const allAddonOptions = useMemo(
    () => OptimizationActionsHelper.getAddonOptions(addons),
    [addons]
  );

  const addonCatalogById = useMemo(() => {
    const m = new Map(addons.map((a) => [a.id, a]));
    return (id: string) => m.get(id);
  }, [addons]);

  const addonScopeForUi =
    action.type === OptimizationActionType.ADDON_PRICE_ADJUSTMENT
      ? (action.scope ?? OptimizationAddonScope.ALL_ADDONS)
      : null;

  const isPercentageAdj =
    isPlanPercentageAdj(action) ||
    (action.type === OptimizationActionType.ADDON_PRICE_ADJUSTMENT &&
      addonScopeForUi === OptimizationAddonScope.ALL_ADDONS &&
      isAddonPercentageAdj(action.adjustment_type));

  const renderMenu = (idx: number) => {
    const hasOnlyOnePlan =
      action.type === OptimizationActionType.PLAN_CHANGE &&
      (action.plans?.length ?? 0) <= 1;

    const handleRemove = () => {
      if (action.type !== OptimizationActionType.PLAN_CHANGE) return;
      onChange(
        produce(action, (draft) => {
          draft.plans?.splice(idx, 1);
        })
      );
    };

    const trashButton = (
      <SButton
        variant="danger-ghost"
        icon={<SIcon name="trash-2" />}
        onClick={handleRemove}
        disabled={hasOnlyOnePlan}
        className="!tw-border-transparent"
      />
    );

    return hasOnlyOnePlan ? (
      <STooltip
        label="At least one plan must remain"
        delayDuration={400}
        placement="left"
        color="light"
      >
        <div>{trashButton}</div>
      </STooltip>
    ) : (
      trashButton
    );
  };

  const renderAddonMenu = (idx: number) => {
    const hasOnlyOneAddon =
      action.type === OptimizationActionType.ADDON_PRICE_ADJUSTMENT &&
      (action.addons?.length ?? 0) <= 1;

    const handleRemove = () => {
      if (action.type !== OptimizationActionType.ADDON_PRICE_ADJUSTMENT) return;
      onChange(
        produce(action, (draft) => {
          draft.addons?.splice(idx, 1);
        })
      );
    };

    const trashButton = (
      <SButton
        variant="danger-ghost"
        icon={<SIcon name="trash-2" />}
        onClick={handleRemove}
        disabled={hasOnlyOneAddon}
        className="!tw-border-transparent"
      />
    );

    return hasOnlyOneAddon ? (
      <STooltip
        label="At least one addon must remain"
        delayDuration={400}
        placement="left"
        color="light"
      >
        <div>{trashButton}</div>
      </STooltip>
    ) : (
      trashButton
    );
  };

  const renderAddonScopeRadios = () => {
    if (action.type !== OptimizationActionType.ADDON_PRICE_ADJUSTMENT)
      return null;

    return (
      <div className="tw-col-span-2">
        <SRadioGroup
          className="[&_.radio-option-label]:!tw-m-0"
          value={addonScopeForUi ?? OptimizationAddonScope.ALL_ADDONS}
          orientation="horizontal"
          onValueChange={(val) => {
            onChange(
              produce(action, (draft) => {
                draft.scope = val as OptimizationAddonScope;
                if (val === OptimizationAddonScope.ALL_ADDONS) {
                  delete draft.addons;
                  draft.adjustment_type ??=
                    OptimizationAddonAdjustmentType.INCREASE_BY_PERCENTAGE;
                  draft.adjustment_value ??= 10;
                } else {
                  delete draft.adjustment_type;
                  delete draft.adjustment_value;
                  draft.addons ??= [
                    OptimizationSliceHelper.createNewAddonAdjustment(),
                  ];
                }
              })
            );
          }}
          options={[
            {
              value: OptimizationAddonScope.ALL_ADDONS,
              label: 'All Addons',
            },
            {
              value: OptimizationAddonScope.SPECIFIC_ADDONS,
              label: 'Specific Addons',
            },
          ]}
        />
      </div>
    );
  };

  const renderAddonAllFields = () => {
    if (
      action.type !== OptimizationActionType.ADDON_PRICE_ADJUSTMENT ||
      addonScopeForUi !== OptimizationAddonScope.ALL_ADDONS
    )
      return null;

    const pct = isAddonPercentageAdj(action.adjustment_type);

    return (
      <>
        <div>
          <SSelect
            className="tw-w-full"
            value={action.adjustment_type}
            onValueChange={(val) => {
              onChange(
                produce(action, (draft) => {
                  draft.adjustment_type =
                    val as unknown as OptimizationAddonAdjustmentType;
                })
              );
            }}
            clearable={false}
            searchable={false}
            options={addonAdjustmentTypeOptions}
            label="Addon adjustment type"
          />
        </div>
        <div>
          <SInput
            className="tw-w-full"
            onChange={(e) => {
              const val = parseFloat(e.currentTarget.value);
              onChange(
                produce(action, (draft) => {
                  draft.adjustment_value =
                    Number.isNaN(val) || val < 0 ? 0 : val;
                })
              );
            }}
            value={`${action.adjustment_value ?? 0}`}
            label={pct ? 'Percentage' : 'Adjustment amount'}
            type="number"
            trailingIcon={pct ? 'percent' : undefined}
            inputSuffix={
              !pct ? (
                <span className="tw-text-sm tw-text-neutral-400">
                  per subscription currency
                </span>
              ) : undefined
            }
          />
        </div>
        {!pct && (
          <div className="tw-col-span-2">
            <SNotification
              icon={null}
              title="Amount is applied in each subscription’s billing currency. For example, 10 becomes $10 for USD subscriptions, €10 for EUR subscriptions, etc."
              variant="warning"
            />
          </div>
        )}
      </>
    );
  };

  const renderAddonSpecificTable = () => {
    if (
      action.type !== OptimizationActionType.ADDON_PRICE_ADJUSTMENT ||
      addonScopeForUi !== OptimizationAddonScope.SPECIFIC_ADDONS
    )
      return null;

    return (
      <div className="tw-col-span-2">
        <STable layout="fixed">
          <STable.Head>
            <STable.Row>
              <STable.HeaderCell className="tw-w-2/12">
                Addon ID
              </STable.HeaderCell>
              <STable.HeaderCell className="tw-w-2/12">
                Adjustment Type
              </STable.HeaderCell>
              <STable.HeaderCell className="tw-w-2/12">Value</STable.HeaderCell>
              <STable.HeaderCell className="tw-w-2/12">
                Current Price
              </STable.HeaderCell>
              <STable.HeaderCell className="tw-w-2/12">
                New Price
              </STable.HeaderCell>
              <STable.HeaderCell className="tw-w-1/12" />
            </STable.Row>
          </STable.Head>
          <STable.Body>
            {action.addons?.map((row, idx) => {
              const rowPct = isAddonPercentageAdj(row.adjustment_type);
              const addonIdsTakenByOtherRows = new Set(
                action.addons
                  ?.filter((_, i) => i !== idx)
                  .map((a) => a.item_price_id)
                  .filter((id): id is string => Boolean(id)) ?? []
              );

              let rowOptions = allAddonOptions.filter(
                (o) =>
                  o.value === row.item_price_id ||
                  !addonIdsTakenByOtherRows.has(o.value)
              );
              if (
                row.item_price_id &&
                !allAddonOptions.some((o) => o.value === row.item_price_id)
              ) {
                rowOptions = [
                  {
                    value: row.item_price_id,
                    label: row.item_price_id,
                    currencyCode: '',
                  },
                  ...rowOptions,
                ];
              }

              const catalogRow = addonCatalogById(row.item_price_id);
              const currentMajor =
                catalogRow !== undefined
                  ? catalogPriceToMajorUnits(Number(catalogRow.price))
                  : undefined;
              const newMajor = previewNewAddonPriceMajorUnits(
                currentMajor,
                row.adjustment_type,
                row.adjustment_value
              );

              return (
                <STable.Row key={idx}>
                  <STable.Cell>
                    <SSelect
                      options={rowOptions}
                      value={row.item_price_id}
                      onValueChange={(e) => {
                        onChange(
                          produce(action, (draft) => {
                            const val = e as string;
                            draft.addons ??= [];
                            draft.addons[idx] ??=
                              OptimizationSliceHelper.createNewAddonAdjustment();
                            draft.addons[idx].item_price_id = val;
                          })
                        );
                      }}
                    />
                  </STable.Cell>
                  <STable.Cell>
                    <SSelect
                      options={addonAdjustmentTypeOptions}
                      value={row.adjustment_type}
                      clearable={false}
                      searchable={false}
                      onValueChange={(val) => {
                        onChange(
                          produce(action, (draft) => {
                            draft.addons ??= [];
                            draft.addons[idx] ??=
                              OptimizationSliceHelper.createNewAddonAdjustment();
                            draft.addons[idx].adjustment_type =
                              val as unknown as OptimizationAddonAdjustmentType;
                          })
                        );
                      }}
                    />
                  </STable.Cell>
                  <STable.Cell>
                    <SInput
                      onChange={(e) => {
                        const val = parseFloat(e.currentTarget.value);
                        onChange(
                          produce(action, (draft) => {
                            draft.addons ??= [];
                            draft.addons[idx] ??=
                              OptimizationSliceHelper.createNewAddonAdjustment();
                            draft.addons[idx].adjustment_value =
                              Number.isNaN(val) || val < 0 ? 0 : val;
                          })
                        );
                      }}
                      value={`${row.adjustment_value}`}
                      type="number"
                      trailingIcon={rowPct ? 'percent' : undefined}
                    />
                  </STable.Cell>
                  <STable.Cell>
                    <span className="tw-text-sm tw-text-neutral-800">
                      {formatAddonPriceCell(
                        currentMajor,
                        catalogRow?.currency_code
                      )}
                    </span>
                  </STable.Cell>
                  <STable.Cell>
                    <span className="tw-text-sm tw-text-neutral-800">
                      {formatAddonPriceCell(
                        newMajor,
                        catalogRow?.currency_code
                      )}
                    </span>
                  </STable.Cell>
                  <STable.Cell>{renderAddonMenu(idx)}</STable.Cell>
                </STable.Row>
              );
            })}
          </STable.Body>
          <STable.Footer>
            <STable.Row>
              <STable.Cell colSpan={5}>
                <SButton
                  variant="primary-ghost"
                  icon={<SIcon name="plus" />}
                  onClick={() => {
                    onChange(
                      produce(action, (draft) => {
                        draft.addons ??= [];
                        draft.addons[draft.addons.length] =
                          OptimizationSliceHelper.createNewAddonAdjustment();
                      })
                    );
                  }}
                >
                  Add Another Addon
                </SButton>
              </STable.Cell>
            </STable.Row>
          </STable.Footer>
        </STable>
      </div>
    );
  };

  const renderedActions =
    action.type === OptimizationActionType.PLAN_PRICE_ADJUSTMENT ? (
      <>
        <div>
          <SSelect
            className="tw-w-full"
            value={action.adjustment_type}
            onValueChange={(val) => {
              onChange(
                produce(action, (draft) => {
                  draft.adjustment_type =
                    val as unknown as OptimizationPriceAdjustmentType;
                })
              );
            }}
            clearable={false}
            searchable={false}
            options={[
              {
                label: 'Increase by percentage',
                value: OptimizationPriceAdjustmentType.INCREASE_BY_PERCENTAGE,
              },
              {
                label: 'Increase by fixed amount',
                value: OptimizationPriceAdjustmentType.INCREASE_BY_FIXED_AMOUNT,
              },
              {
                label: 'Decrease by percentage',
                value: OptimizationPriceAdjustmentType.DECREASE_BY_PERCENTAGE,
              },
              {
                label: 'Decrease by fixed amount',
                value: OptimizationPriceAdjustmentType.DECREASE_BY_FIXED_AMOUNT,
              },
            ]}
            label="Plan Adjustment Type"
          />
        </div>
        <div>
          <SInput
            className="tw-w-full"
            onChange={(e) => {
              const val = parseFloat(e.currentTarget.value);

              onChange(
                produce(action, (draft) => {
                  draft.adjustment_value =
                    Number.isNaN(val) || val < 0 ? 0 : val;
                })
              );
            }}
            value={`${action.adjustment_value}`}
            label={isPercentageAdj ? 'Percentage' : 'Adjustment amount'}
            type="number"
            trailingIcon={isPercentageAdj ? 'percent' : undefined}
            append={
              !isPercentageAdj ? (
                <span className="tw-text-sm tw-text-neutral-400">
                  per subscription currency
                </span>
              ) : undefined
            }
          />
        </div>
        {!isPercentageAdj && (
          <div className="tw-col-span-2">
            <SNotification
              icon={null}
              title="Amount is applied in each subscription’s billing currency. For example, 10 becomes $10 for USD plans, €10 for EUR plans, etc."
              variant="warning"
            />
          </div>
        )}
      </>
    ) : action.type === OptimizationActionType.ADDON_PRICE_ADJUSTMENT ? (
      <>
        {renderAddonScopeRadios()}
        {addonScopeForUi === OptimizationAddonScope.ALL_ADDONS
          ? renderAddonAllFields()
          : renderAddonSpecificTable()}
      </>
    ) : action.type === OptimizationActionType.PLAN_CHANGE ? (
      <div className="tw-col-span-2">
        <STable layout="fixed">
          <STable.Head>
            <STable.Row>
              <STable.HeaderCell className="tw-w-5/12">
                Current Plan
              </STable.HeaderCell>
              <STable.HeaderCell className="tw-w-5/12">
                Target Plan
              </STable.HeaderCell>
              <STable.HeaderCell />
            </STable.Row>
          </STable.Head>
          <STable.Body>
            {action.plans?.map((pl, idx) => {
              const currentCurrency = allPlansOptions.find(
                (o) => o.value === pl.current_plan
              )?.currencyCode;
              const targetCurrency = allPlansOptions.find(
                (o) => o.value === pl.target_plan
              )?.currencyCode;

              const currentPlanOptions = targetCurrency
                ? OptimizationActionsHelper.filterPlanOptionsByCurrency(
                    allPlansOptions,
                    targetCurrency
                  )
                : allPlansOptions;

              const targetPlanOptions = currentCurrency
                ? OptimizationActionsHelper.filterPlanOptionsByCurrency(
                    allPlansOptions,
                    currentCurrency
                  )
                : allPlansOptions;

              return (
                <STable.Row key={idx}>
                  <STable.Cell>
                    <SSelect
                      options={currentPlanOptions}
                      value={pl.current_plan}
                      onValueChange={(e) => {
                        onChange(
                          produce(action, (draft) => {
                            const val = e as string;
                            draft.plans ??= [];
                            draft.plans[idx] ??=
                              OptimizationSliceHelper.createNewPlanChangePlan();
                            draft.plans[idx].current_plan = val;
                          })
                        );
                      }}
                    />
                  </STable.Cell>
                  <STable.Cell>
                    <SSelect
                      options={targetPlanOptions}
                      value={pl.target_plan}
                      onValueChange={(e) => {
                        onChange(
                          produce(action, (draft) => {
                            const val = e as string;
                            draft.plans ??= [];
                            draft.plans[idx] ??=
                              OptimizationSliceHelper.createNewPlanChangePlan();
                            draft.plans[idx].target_plan = val;
                          })
                        );
                      }}
                    />
                  </STable.Cell>
                  <STable.Cell>{renderMenu(idx)}</STable.Cell>
                </STable.Row>
              );
            })}
          </STable.Body>
          <STable.Footer>
            <STable.Row>
              <STable.Cell colSpan={2}>
                <SButton
                  variant="primary-ghost"
                  icon={<SIcon name="plus" />}
                  onClick={() => {
                    onChange(
                      produce(action, (draft) => {
                        draft.plans ??= [];
                        draft.plans[draft.plans.length] =
                          OptimizationSliceHelper.createNewPlanChangePlan();
                      })
                    );
                  }}
                >
                  Add Another Plan
                </SButton>
              </STable.Cell>
            </STable.Row>
          </STable.Footer>
        </STable>
      </div>
    ) : null;

  return (
    <>
      {!actionsOnly && (
        <>
          <div className="tw-p-6">
            <SInput
              value={action.display_name}
              onChange={(e) => {
                const val = e.currentTarget.value;
                onChange(
                  produce(action, (draft) => {
                    draft.display_name = val;
                  })
                );
              }}
              label="Rule Name"
              placeholder="Enter rule name..."
            />
          </div>
          <div className="tw-p-6">
            <ConditionsGroup path={['audience']} maxLevels={1} />
          </div>
        </>
      )}
      <div className={cs({ 'tw-p-6': !actionsOnly })}>
        {!actionsOnly && <div className="tw-mb-sm tw-text-xs">Actions</div>}
        <div className="tw-grid tw-grid-cols-2 tw-gap-sm">
          <div>
            <SSelect
              className="tw-w-full"
              value={action.type}
              onValueChange={(val) => {
                onChange(
                  produce(action, (draft) => {
                    draft.type = val as unknown as OptimizationActionType;
                    OptimizationSliceHelper.cleanupActionFields(draft);
                    switch (draft.type) {
                      case OptimizationActionType.PLAN_PRICE_ADJUSTMENT:
                        draft.adjustment_type ??=
                          OptimizationPriceAdjustmentType.INCREASE_BY_PERCENTAGE;
                        draft.adjustment_value ??= 10;
                        break;
                      case OptimizationActionType.PLAN_CHANGE:
                        draft.plans ??= [
                          OptimizationSliceHelper.createNewPlanChangePlan(),
                        ];
                        break;
                      case OptimizationActionType.ADDON_PRICE_ADJUSTMENT:
                        draft.scope ??= OptimizationAddonScope.ALL_ADDONS;
                        draft.adjustment_type ??=
                          OptimizationAddonAdjustmentType.INCREASE_BY_PERCENTAGE;
                        draft.adjustment_value ??= 10;
                        break;
                      default:
                        break;
                    }
                  })
                );
              }}
              clearable={false}
              searchable={false}
              options={[
                {
                  label: 'Plan Price Adjustment',
                  value: OptimizationActionType.PLAN_PRICE_ADJUSTMENT,
                },
                {
                  label: 'Plan Change',
                  value: OptimizationActionType.PLAN_CHANGE,
                },
                {
                  label: 'Addon Price Adjustment',
                  value: OptimizationActionType.ADDON_PRICE_ADJUSTMENT,
                },
              ]}
              label="Type"
            />
          </div>
          {renderedActions}
        </div>
      </div>
    </>
  );
};
